import createTemplate from './createTemplate';
import selectPartialsFromHTML from './selectPartialsFromHTML';
import getPartial from './getPartial';
import generatePath from './generatePath';
import generateLayoutTemplateHTMLContent from './generateLayoutTemplateHTMLContent';
import { cssGlobalFilenameSuffix, cssTabletFilenameSuffix, cssMobileFilenameSuffix } from "../../../config/fileSuffixes";
import { getScssImportsWithRelativePath } from './getScssImports';
import { definedPages } from '../../../config/defaults';

//keeps track of templates and partials
//abstracts adding compound templates (objects containing html, css, etc.)\
class AbstractTemplateManager {
    constructor() {
        //template objects created by ./createTemplate
        this.cssJsPaths = new Set();
        this.jsContents = new Map();
        this.templates = new Map();
        this.addedPartials = new Set();
        this.staticFilePaths = new Set();
        this.layoutTemplatesReady = false;
        this.headerLocations = {};
        this.footerLocations = {};
        this.layoutJs = "";
        this.layoutPreloadJsInclude = [];
        this.layoutJsInclude = [];
    }

    getTemplatePageNames(templateObject) {
        let templatePagesArray = [];
        if (templateObject.pages) {
            if (Array.isArray(templateObject.pages)) {
                templatePagesArray = [...templateObject.pages];
            }
            if (!Array.isArray(templateObject.pages) && typeof templateObject.pages == "string") {
                templatePagesArray.push(templateObject.pages);
            }
        }
        return templatePagesArray;
    }

    getMapContent(contentsMap, key) {
        if (contentsMap.has(key)) {
            return contentsMap.get(key);
        }
        return '';
    }

    processStaticFile(file) {
        if (this.templates.has(file.name)) {
            throw new Error(`In staticFiles: file ${file.name} already exists`)
        }
        this.staticFilePaths.add(file.name);
        this.templates.set(file.name, createTemplate(file.name, file.text, file.encoded));
    }

    partialPathIteration = partialPath => {
        if (this.addedPartials.has(partialPath)) {
            return;
        }
        this.addedPartials.add(partialPath);
        const templateObject = getPartial(partialPath);
        if (!templateObject) {
            return; //or throw
        }
        //cut 'partials/' directory from the beginning and '.html' file extension from the end
        this.generateObjectTemplate("partials", partialPath.slice(9, -5), templateObject);
    }
    /**
     * Looks for partials defined in the object template "relatedPartials" field
     * @param {[]} relatedPartials 
     */
    generateRelatedPartials(relatedPartials) {
        if (!Array.isArray(relatedPartials)) {
            return;
        }
        relatedPartials.forEach(this.partialPathIteration);
    }

    /**
     * orders scan of liquid includes of partials, then checks if it already has one and adds a partial when it doesn't have it.
     * @param {string} htmlContent 
     */
    scanPartials(htmlContent) {
        const partials = selectPartialsFromHTML(htmlContent);
        partials.forEach(this.partialPathIteration);
    }

    generateHtmlTemplate(locationPrefix, location, templateObject) {
        const fullPathHTML = generatePath(locationPrefix, location, templateObject.id || "", "html");
        if (location === "header") {
            this.headerLocations.html = fullPathHTML;
        }
        if (location === "footer") {
            this.footerLocations.html = fullPathHTML;
        }

        if (this.templates.has(fullPathHTML)) {
            return; //or throw, not sure
        }
        const htmlContent = templateObject.html || "";
        this.scanPartials(htmlContent);
        this.templates.set(fullPathHTML, createTemplate(fullPathHTML, htmlContent));
    }
    
    settingCssJsContentsMap(contentMap, key, value) {
        if (contentMap.has(key)) {
            contentMap.set(key, contentMap.get(key).concat(value));
        } else {
            contentMap.set(key, value);
        }
    }
    
    settingDefinedPagesCssJsContent(templateObject, contentMap, jsContent) {
        const templateObjectPagesArray = this.getTemplatePageNames(templateObject); 
        templateObjectPagesArray.forEach(page => {
            for (const [definedPage] of Object.entries(definedPages)) {
                if (page !== definedPage) {
                    continue;
                }
                this.settingCssJsContentsMap(contentMap, definedPage, jsContent);
                break;
            }
        });
    }

    generateJsTemplate(locationPrefix, location, templateObject) {
        let jsPrefix;
        if (!!locationPrefix) {
            jsPrefix = "js/" + locationPrefix;
        } else {
            jsPrefix = "js";
        }
        const fullPathJS = generatePath(jsPrefix, location, templateObject.id || "", "js");
        const jsContent = templateObject.js || "";

        if (!jsContent) {
            return;
        }
        if (this.cssJsPaths.has(fullPathJS)) {
            return;
        }

        if (location === "header") {
            this.headerLocations.js = jsContent;
            this.cssJsPaths.add(fullPathJS);
            return;
        }
        if (location === "footer") {
            this.footerLocations.js = jsContent;
            this.cssJsPaths.add(fullPathJS);
            return;
        } 

        if (templateObject.pages) {
            this.settingDefinedPagesCssJsContent(templateObject, this.jsContents, jsContent);      
            this.cssJsPaths.add(fullPathJS);
            return;
        }

        const jsContent1 = this.getMapContent(this.jsContents, 'Content1');
        const jsContent2 = this.getMapContent(this.jsContents, 'Content2');

        const js = this.layoutJs + (this.headerLocations.js || "") + (this.footerLocations.js || "") + jsContent1;
        const newJsSize = js.length + jsContent.length;
        const maxJsSize = 256000;

        if (newJsSize > maxJsSize) {
            if ((jsContent2.length + jsContent.length) > maxJsSize) {
                this.settingCssJsContentsMap(this.jsContents, 'Content3', jsContent);
            } else {
                this.settingCssJsContentsMap(this.jsContents, 'Content2', jsContent);
            }
        } else {
            this.settingCssJsContentsMap(this.jsContents, 'Content1', jsContent);
        }
        this.cssJsPaths.add(fullPathJS);
    }

    //locationPrefix - used to denote origin in template path, ie. staticElements,elements, partials
    //location - path to file (can be a whole subbranch)
    //templateObject - required/imported object with html,css,scss,js fields specified
    generateObjectTemplate(locationPrefix, location, templateObject) {
        //paths are going to be different with different types of objects
        //partials should be detected only in html
        this.generateHtmlTemplate(locationPrefix, location, templateObject);
        this.generateCssGlobalTemplate(locationPrefix, location, templateObject);
        this.generateCssMobileTemplate(locationPrefix, location, templateObject);
        this.generateCssTabletTemplate(locationPrefix, location, templateObject);

        this.generateJsTemplate(locationPrefix, location, templateObject);

        this.generateScssTemplate(locationPrefix, location, templateObject, cssGlobalFilenameSuffix, "scss");
        this.generateScssTemplate(locationPrefix, location, templateObject, cssTabletFilenameSuffix, "scssTablet");
        this.generateScssTemplate(locationPrefix, location, templateObject, cssMobileFilenameSuffix, "scssMobile");
        this.generateRelatedPartials(templateObject.relatedPartials);
    }

    generateLayoutExtensionTemplate(includeString, templateName, isExtends = true, additionalOuterContainer = null) {
        if (!templateName) {
            throw new Error("Template name not provided");
        }
        let html = "";
        if (isExtends) {
            html += "{% extends '_layout.html' -%}\n\n";
            html += "{% block pageContent -%}\n"
            if(additionalOuterContainer!==null) {
                html += additionalOuterContainer.top;
                html += includeString;
                html += additionalOuterContainer.bottom;
            }
            else {
                html += includeString;
            }
            html += "{% endblock -%}\n";
        } else {
            html += includeString;
        }
        
        this.templates.set(templateName, createTemplate(templateName, html));
    }

    generateLayoutHTMLTemplate(fontName = "Roboto") {
        const templateName = "_layout.html";
        const html = generateLayoutTemplateHTMLContent(this.headerLocations, this.footerLocations, this.layoutPreloadJsInclude, this.layoutJsInclude, this.layoutPreloadCssInclude, this.layoutCssGlobalInclude, this.layoutCssTabletInclude, this.layoutCssMobileInclude, fontName);
        this.templates.set(templateName, createTemplate(templateName, html));
    }
    getBaseLayout() {
        throw new Error("Abstract method call");
    }
    generateLayoutCSSGlobalTemplate() {
        throw new Error("Abstract method call");
    }
    generateLayoutCSSTabletTemplate() {
        throw new Error("Abstract method call");
    }
    generateLayoutCSSMobileTemplate() {
        throw new Error("Abstract method call");
    }

    generateLayoutJSTemplate(onlyFile = false, abstractFileVersion = '') {
        const scripts = [this.layoutJs, this.headerLocations.js || "", this.footerLocations.js || "", this.jsContents.get("Content1") || "", this.jsContents.get("Content2") || "", this.jsContents.get("Content3") || ""];
        const filesArr = new Map();
        let baseFilesArr = ["","","",""];
        for (const script of scripts) {
            baseFilesArr = this.assignScriptToFile(script, baseFilesArr);
        }
        filesArr.set("", baseFilesArr);
        this.jsContents.forEach((jsValue, jsKey) => {
            if (jsKey !== "Content1" && jsKey !== "Content2" && jsKey !== "Content3") {
                let pageFilesArr = ["","","",""];
                pageFilesArr = this.assignScriptToFile(jsValue, pageFilesArr);
                filesArr.set(jsKey, pageFilesArr);
            }
        })
        const layoutPreloadJsIncludeTemp = [];
        const layoutJsIncludeTemp = [];
        filesArr.forEach((fileArray, fileSubName) => {
            for(let i = 0; i < fileArray.length; i++) {
                if(fileArray[i].length > 0){
                    let pathMiddle = fileSubName;
                    if (fileSubName) {
                        pathMiddle = `-${fileSubName.toLowerCase()}`;
                    }
                    const path = `js/layout${pathMiddle}${i}.js`;
                    const template = createTemplate(path, fileArray[i]);
                    this.templates.set(path, template);
                    if(!onlyFile) {
                        let includeJsString = '<script src="' + path + '?v=' + abstractFileVersion + '_{{ lqTS }}"></script>';
                        let includePreloadJsString = '<link rel="preload" href="' + path + '?v=' + abstractFileVersion + '_{{ lqTS }}" as="script">';
                        if (fileSubName) {
                            includeJsString = `{% if page.PageId == ${definedPages[fileSubName]} -%}\n` + includeJsString + '\n{% endif -%}';
                            includePreloadJsString = `{% if page.PageId == ${definedPages[fileSubName]} -%}\n` + includePreloadJsString + '\n{% endif -%}';
                        }
                        layoutJsIncludeTemp.push(includeJsString);
                        layoutPreloadJsIncludeTemp.push(includePreloadJsString);
                    }
                }
            }
            if (!fileSubName) {
                layoutJsIncludeTemp.push(...this.layoutJsInclude);
                layoutPreloadJsIncludeTemp.push(...this.layoutPreloadJsInclude);
            }
        });
        this.layoutJsInclude = [...layoutJsIncludeTemp];
        this.layoutPreloadJsInclude = [...layoutPreloadJsIncludeTemp];
    }

    assignScriptToFile(script, filesArr) {
        const maxJsSize = 256000;
        let js0 = filesArr[0];
        let js1 = filesArr[1];
        let js2 = filesArr[2];
        let js3 = filesArr[3];
        if(script.length > 0){
            if ((js0.length + script.length) > maxJsSize) {
                if ((js1.length + script.length) > maxJsSize) {
                    if ((js2.length + script.length) > maxJsSize) {
                        js3 += script;
                    } else {
                        js2 += script;
                    }
                } else {
                    js1 += script;
                }
            } else {
                js0 += script;
            }
        }
        return [js0, js1, js2, js3];
    }

    generateLayoutTemplates() {
        throw new Error("Abstract method call");
    }

    generateStaticFiles(spinner) {
        const staticFiles = require("../../../assets/staticFiles").default;
        const possibleDuplicateJsFiles = staticFiles.filter(file => file.name.includes('.min.js'));
        staticFiles.forEach(file => {
            if (possibleDuplicateJsFiles.find(possibleDuplicateJsFile => possibleDuplicateJsFile.name === file.name)) {
                return;
            }
            this.processStaticFile(file)
        })
        //file to generate depends on spinnerIcon
        let spinnerImageText;
        if (spinner.style && spinner.icon) {
            spinnerImageText = require(`../../../assets/staticFiles/css/img/b64_spinner/b64_${spinner.icon}-${spinner.style}.gif`).default;
        } else {
            spinnerImageText = require("../../../assets/staticFiles/css/img/b64_alo.gif").default;
        }
        
        this.processStaticFile({
            "name": "css/img/alo.gif",
            "encoded": true,
            "text": spinnerImageText
        });
    }

    getTemplates(spinner, fontName = "Roboto") {
        if (this.staticFilePaths.size === 0) {
            this.generateStaticFiles(spinner);
        }
        if (!this.layoutTemplatesReady) {
            this.generateLayoutTemplates(fontName);
        }
        return Array.from(this.templates.values());
    }

    generateScssTemplate(locationPrefix, location, templateObject, cssFilenameSuffix, scssField) {
        let scssPrefix;
        if (!!locationPrefix) {
            scssPrefix = "scss/" + locationPrefix;
        } else {
            scssPrefix = "scss";
        }
        const fullPathSCSS = generatePath(scssPrefix, location, templateObject.id || "", "scss", cssFilenameSuffix);
        if (location === "header") {
            this.headerLocations[scssField] = fullPathSCSS;
        }
        if (location === "footer") {
            this.footerLocations[scssField] = fullPathSCSS;
        }

        if (this.templates.has(fullPathSCSS)) {
            return; //or throw, not sure
        }

        const imports = getScssImportsWithRelativePath(fullPathSCSS);
        let scssContent = templateObject[scssField] || "";
        if (!Object.keys(templateObject).includes(scssField)) {
            return;
        }
        scssContent = imports + scssContent;
        this.templates.set(fullPathSCSS, createTemplate(fullPathSCSS, scssContent));
    }
}

export default AbstractTemplateManager